import {
  BatchWiseStockApiResponse,
  OrderType,
  RealTimeStockApiResponse,
  RealTimeStockRowsData,
  StatusFilterMenuItems,
  TransformedBatch,
  TransformedRealTimeStock,
} from "../../../types/types";
import { ChannelkartNetworkGet } from "../../../services/networkService";
import { deepClone } from "@mui/x-data-grid/utils/utils";
import {
  clearAllDataFromLocalDb,
  getDataFromLocalDb,
  insertDataIntoLocalDB,
} from "../../../services/dbService";
import { Catalogue, Product } from "../../createSecondaryOrder/models";
import { EnvironmentRegistry } from "../../../services/registry/environmentRegistry";
import { getConfigData } from "../../../services/configurationService";
import { createCatalogue, getPricingData } from "../../../services/catalogueService";
import { sendSlackAlert, SlackChannel } from "../../../services/slackService";
import { safeStringify } from "../../../components/dms_common/DmsUtils";
import { fetchPaginatedStockData } from "../../createSecondaryOrder/CreateSecondaryOrderService";

// Function to fetch pricing data for a specific distributor
export const fetchDistributorPricing = async (distributorId: string) => {
  try {
    // const response = await ChannelkartNetworkGet(`/productmetadata/v1/items/outlet?minimalResponse=true&filter=activeStatus:active&loginId=${distributorId}&size=10000`);
    // const pricingData = response?.data?.features || [];
    const pricingData = await getPricingData(undefined, undefined, OrderType.PRIMARY, false, distributorId, true);
    // Create a map of batchCode to pricing data for easy lookup
    const pricingMap: Record<string, any> = {};
    pricingData.forEach((item: any) => {
      pricingMap[item.batchCode] = {
        casePtr: item.casePtr || 0,
        packPtr: item.packPtr || 0,
        otherUnitPtr: item.otherUnitPtr || 0
      };
    });
    
    return pricingMap;
  } catch (error) {
    console.error("Error fetching distributor pricing data:", error);
    return {};
  }
};

export const getDistributorList = async (): Promise<StatusFilterMenuItems[]> => {
  try {
    const response = await ChannelkartNetworkGet(`/users/mapped/hierarchy?filter=activeStatus:active and designation[in]:[distributor,supplier,superstockist,subdistributor,bigdistributor]&size=10000`);

    const distributors = response?.data?.features || [];
    
    // Format distributors - maintain full label for tooltip/hover
    const formattedDistributors = distributors.map((distributor: any) => ({
      id: distributor.loginId,
      label: `${distributor.loginId} - ${distributor.name}`
    }));
    
    // Sort distributors by ID (distributor code) in ascending order
    const sortedDistributors = formattedDistributors.sort((a: StatusFilterMenuItems, b: StatusFilterMenuItems) => 
      a.id.localeCompare(b.id)
    );
    
    return sortedDistributors;
  } catch (error) {
    console.error("Error fetching distributor list:", error);
    return [];
  }
};

export const formatToThousands = (num: number): string => {
  const truncatedValue = Math.floor((num / 1000) * 10) / 10;
  return truncatedValue.toFixed(1).replace(/\.?0+$/, "") + "K";
};

export const transformSkuWiseData = (
  apiData: RealTimeStockApiResponse[]
): TransformedRealTimeStock[] => {
  const batchMap: { [key: string]: TransformedRealTimeStock } = {};
  apiData.forEach((item) => {
    const { skuCode, quantityType, type, ...rest } = item;
    if (!batchMap[skuCode+'-'+type]) {
      batchMap[skuCode+'-'+type] = {
        id: skuCode+'-'+type,
        skuCode: skuCode,
        type: type
      };
    }
    batchMap[skuCode+'-'+type][quantityType] = { ...rest };
  });
  return Object.values(batchMap);
};

export const setRealTimeStockDataInDb = async (skuCodes: string = "", quantityType: string = "", selectedDistributor?: string, warehouseId?: string, type?: string) => {
  const skuCodesQueryParam = skuCodes?.length ? `&skuCode=${skuCodes}` : ``;
  
  // Get user context
  const authContext = JSON.parse(localStorage.authContext);
  const currentUser = authContext.user;
  const currentDesignation = currentUser.designation || [];
  const useSelectedDistributorIfDesignationsConfig = await getConfigData("dms_configuration", "useSelectedDistributorIfDesignations");
  const useSelectedDistributorIfDesignations = useSelectedDistributorIfDesignationsConfig?.[0]?.configValue?.split(",") 
    ?? ["company", "admin", "projectadmin", "reportadmin"];

  // Normalize currentDesignation to an array
  const designationList = Array.isArray(currentDesignation)
    ? currentDesignation
    : [currentDesignation];

  // Check if any relevant designation is present
  const isCompanyOrAdmin = useSelectedDistributorIfDesignations?.some((designation: string) =>
    designationList?.includes(designation)
  );

  const useSelectedDistributor = isCompanyOrAdmin && selectedDistributor;

  const supplier = useSelectedDistributor
    ? selectedDistributor 
    : currentUser.loginId;
  
  const typeFilter = type !== undefined ? `&type=${type}` : ""
  
  try {
    const response = await ChannelkartNetworkGet(
      `${EnvironmentRegistry.getStockBaseUrl()}/consolidatedStock/getStockInfo?${quantityType ? "quantityType=" + quantityType+"&" : ""}minimalResponse=true&supplier=${supplier}${skuCodesQueryParam}${warehouseId ? "&warehouseId=" + warehouseId : ""}${typeFilter}&type=normal`
    );
    const transformedData = transformSkuWiseData(response?.data["content"]);

    // do not set data to local db when skuCodes are passed
    if ((skuCodes ?? "")?.length) return transformedData;

    await clearAllDataFromLocalDb("RealTimeStockData");
    await insertDataIntoLocalDB("RealTimeStockData", transformedData);
  } catch (err) {
    const url = `${EnvironmentRegistry.getStockBaseUrl()}/consolidatedStock/getStockInfo?${quantityType ? "quantityType=" + quantityType+"&" : ""}minimalResponse=true&supplier=${supplier}${skuCodesQueryParam}${warehouseId ? "&warehouseId=" + warehouseId : ""}${typeFilter}&type=normal`
    await sendSlackAlert(
      SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
      "Error fetching data from API",
      `Failed to fetch data from the API: ${url}`,
      safeStringify({ err,url})
    );
    console.log("Error calling Real time stock api");
    throw new Error()
  }
};

const transformBatchData = (apiData: BatchWiseStockApiResponse[]): TransformedBatch[] => {
  const batchMap: { [key: string]: TransformedBatch } = {};
  apiData.forEach((item) => {
    const { batchId, skuCode, quantityType, ...rest } = item;
    const skuBatchKey = `${skuCode}-${batchId}`;
    if (!batchMap[skuBatchKey]) {
      batchMap[skuBatchKey] = {
        id: skuBatchKey,
        batchId: batchId,
        skuCode,
      };
    }
    
    // Construct proper QuantityDetail object with all required properties
    const quantityDetail = {
      committedQty: rest.committedQty ?? 0,
      availableQty: rest.availableQty ?? 0,
      unsellableQty: rest.unsellableQty ?? 0,
      inTransitQty: rest.inTransitQty ?? 0,
      onRouteQty: rest.onRouteQty ?? 0,
      returnedQty: rest.returnedQty ?? 0,
      lastModifiedTime: rest.lastModifiedTime ?? "",
      expiryDate: rest.expiryDate
    };
    
    // Use proper type assertion to handle the dynamic property assignment
    (batchMap[skuBatchKey] as any)[quantityType] = quantityDetail;
  });
  return Object.values(batchMap);
};

export const fetchBatchDataForSkuList = async (skuCodes: string, quantityType: string = "") => {
  const skuCodesQueryParam = skuCodes?.length ? `&skuCode=${skuCodes}` : ``;
  try {
    // Get user context
    const authContext = JSON.parse(localStorage.authContext);
    const currentUser = authContext.user;
    const supplier = currentUser.loginId

    const response = await ChannelkartNetworkGet(
      `${EnvironmentRegistry.getStockBaseUrl()}/consolidatedStock/getGroupedBatchWiseStockInfo?${quantityType ? "quantityType=" + quantityType+"&" : ""}supplier=${supplier}&minimalResponse=true${skuCodesQueryParam}&type=normal`
    );
    
    const content = response.data.content ?? [];
    const transformedData = transformBatchData(content);
    return transformedData
  } catch (error) {
    console.error("Error fetching batch data for SKU:", error);
  }
};


export const fetchBatchWiseStock = async () => {
 
  try {
    const authContext = JSON.parse(localStorage.authContext);
    const currentUser = authContext.user;
    const supplier = currentUser.loginId
    const size = 10000

    const filter = `${EnvironmentRegistry.getStockBaseUrl()}/consolidatedStock/getGroupedBatchWiseStockInfo?supplier=${supplier}&minimalResponse=true&type=normal`
    
    const response = await fetchPaginatedStockData(filter, size) as any;

    return response
  } catch (error) {
    console.error("Error fetching batch wise stock data:", error);
  }
};

export const getMergedCatalogueAndRealTimeStockData = (
  stockData: RealTimeStockRowsData[] | any,
  productsMap: Record<string, Product>
) => {

  return stockData.map((item : any ) => {
    const product = productsMap[item.skuCode];
    return {
      ...item,
      skuDescription: product?.skuDescription ?? "",
      brand: product?.brand ?? "",
      category: product?.category ?? "",
      case2Piece: product?.case2Piece ?? 1,
      shelfLife: product?.shelfLifeDays ?? 0,
      otherUnitName: product?.otherUnitName,
    };
  });
};

export const realTimeStockColumnsMetadata = {
  get columns() {
    const allColumns = [
      {
        headerName: "Last Updated On",
        mappedValue: "lastModifiedTime",
        width: 190,
        headerAlign: "left",
        align: "left",
        paddingLeft: "10px",
      },
      {
        headerName: "SKU Code",
        mappedValue: "skuCode",
        width: 120,
        headerAlign: "left",
        align: "left",
      },
      {
        headerName: "SKU Name",
        mappedValue: "skuDescription",
        width: 250,
        headerAlign: "left",
        align: "left",
        truncateTextAfterLine: 2,
      },
      {
        headerName: "Brand",
        mappedValue: "brand",
        width: 100,
        headerAlign: "left",
        align: "left",
        paddingLeft: "10px",
      },
      {
        headerName: "Category",
        mappedValue: "category",
        width: 160,
        headerAlign: "left",
        align: "left",
      },
      {
        headerName: "Batch ID",
        mappedValue: "batchId",
        width: 160,
        headerAlign: "left",
        align: "left",
        paddingRight: "10px",
      },
      {
        headerName: "Total Stock",
        mappedValue: "category",
        width: 120,
        headerAlign: "left",
        align: "left",
        paddingLeft: "10px",
      },
    ];

    // Check if batch ID should be shown based on the configuration
    const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
    // Default to true if not present in ConfigDictionary
    const showBatchId = ConfigDictionary.showBatchId ?? true;
    
    // If showBatchId is false, filter out the Batch ID column
    return showBatchId 
      ? allColumns 
      : allColumns.filter(column => column.headerName !== "Batch ID");
  },
};

const calculateTotalSummary = (data: StockSummaryData) => {
  let totalCs = 0;
  let totalPc = 0;
  let totalOther = 0;
  let totalAmount = 0;

  Object.values(data.stockDetails).forEach(({ cs, pc, other, amount }) => {
    totalCs += cs;
    totalPc += pc;
    totalOther += other;
    totalAmount += amount;
  });

  data.totalStockSummary.totalCs = totalCs;
  data.totalStockSummary.totalPc = totalPc;
  data.totalStockSummary.totalOther = totalOther;
  data.totalStockSummary.totalAmount = totalAmount;
};

const calculateTotalAmount = (
  item: any,
  cs: number = 0,
  pc: number = 0,
  other: number = 0,
  distributorPricing?: Record<string, any>
): number => {
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  
  // If we have distributor-specific pricing and this item exists in it, use that pricing
  if (distributorPricing && item && distributorPricing[item.batchCode]) {
    const pricing = distributorPricing[item.batchCode];
    if (ConfigDictionary.otherUnitLabel) {
      return pricing.casePtr * cs + pricing.packPtr * pc + pricing.otherUnitPtr * other;
    } else {
      return pricing.casePtr * cs + pricing.packPtr * pc;
    }
  } 
  // Otherwise fall back to the existing logic
  else if (item && ConfigDictionary.otherUnitLabel) {
    return item.casePtr * cs + item.packPtr * pc + item.otherUnitPtr * other;
  } else if (item) {
    return item.casePtr * cs + item.packPtr * pc;
  } else return 0;
};

export const getStockSummaryTileData = async (selectedDistributor?: string) => {
  // Get user context
  const authContext = JSON.parse(localStorage.authContext);
  const currentUser = authContext.user;
  const currentDesignation = currentUser.designation || [];
  const useSelectedDistributorIfDesignationsConfig = await getConfigData("dms_configuration", "useSelectedDistributorIfDesignations");
  const useSelectedDistributorIfDesignations = useSelectedDistributorIfDesignationsConfig?.[0]?.configValue?.split(",") 
    ?? ["company", "admin", "projectadmin", "reportadmin"];

  // Normalize currentDesignation to an array
  const designationList = Array.isArray(currentDesignation)
    ? currentDesignation
    : [currentDesignation];

  // Check if any relevant designation is present
  const isCompanyOrAdmin = useSelectedDistributorIfDesignations?.some((designation: string) =>
    designationList?.includes(designation)
  );

  const useSelectedDistributor = isCompanyOrAdmin && selectedDistributor;
  
  // Fetch distributor-specific pricing if needed
  let distributorPricing: Record<string, any> = {};
  if (useSelectedDistributor) {
    distributorPricing = await fetchDistributorPricing(selectedDistributor);
  }

  let catalogueData = await getDataFromLocalDb("PrimaryCatalogue");
  if(catalogueData.data.length === 0){
    try{
      await createCatalogue(
        OrderType.PRIMARY,
        JSON.parse(localStorage.authContext)?.user?.loginId,
        false,
        undefined,
        undefined,
        undefined,
        true,
      )
    }catch(err){
      console.error("Error creating catalogue", err);
    }
    catalogueData = await getDataFromLocalDb("PrimaryCatalogue");
  } 
  const catalogueMap = new Map<string, Catalogue>();
  catalogueData.data.forEach((item: Catalogue) => {
    catalogueMap.set(item.batchCode, item);
  });

  const tileData: StockSummaryData = deepClone(defaultStockSummaryTileData);
  const realTimeStockData = await getDataFromLocalDb("RealTimeStockData");

  realTimeStockData.data.forEach((item: TransformedRealTimeStock) => {
    const catalogueItem = useSelectedDistributor ? {...item, batchCode: item.skuCode, casePtr: 0, packPtr: 0, otherUnitPtr: 0} : catalogueMap.get(item.skuCode);

    const { caseQty, pieceQty, otherQty } = item;

    tileData.stockDetails.availableQty.pc += pieceQty?.availableQty ?? 0;
    tileData.stockDetails.availableQty.cs += caseQty?.availableQty ?? 0;
    tileData.stockDetails.availableQty.other += otherQty?.availableQty ?? 0;
    tileData.stockDetails.availableQty.amount += calculateTotalAmount(
      catalogueItem,
      caseQty?.availableQty,
      pieceQty?.availableQty,
      otherQty?.availableQty,
      distributorPricing
    );

    tileData.stockDetails.committedQty.pc += pieceQty?.committedQty ?? 0;
    tileData.stockDetails.committedQty.cs += caseQty?.committedQty ?? 0;
    tileData.stockDetails.committedQty.other += otherQty?.committedQty ?? 0;
    tileData.stockDetails.committedQty.amount += calculateTotalAmount(
      catalogueItem,
      caseQty?.committedQty,
      pieceQty?.committedQty,
      otherQty?.committedQty,
      distributorPricing
    );

    tileData.stockDetails.onRouteQty.pc += pieceQty?.onRouteQty ?? 0;
    tileData.stockDetails.onRouteQty.cs += caseQty?.onRouteQty ?? 0;
    tileData.stockDetails.onRouteQty.other += otherQty?.onRouteQty ?? 0;
    tileData.stockDetails.onRouteQty.amount += calculateTotalAmount(
      catalogueItem,
      caseQty?.onRouteQty,
      pieceQty?.onRouteQty,
      otherQty?.onRouteQty,
      distributorPricing
    );

    tileData.stockDetails.returnedQty.pc += pieceQty?.returnedQty ?? 0;
    tileData.stockDetails.returnedQty.cs += caseQty?.returnedQty ?? 0;
    tileData.stockDetails.returnedQty.other += otherQty?.returnedQty ?? 0;
    tileData.stockDetails.returnedQty.amount += calculateTotalAmount(
      catalogueItem,
      caseQty?.returnedQty,
      pieceQty?.returnedQty,
      otherQty?.returnedQty,
      distributorPricing
    );

    tileData.stockDetails.unsellableQty.pc += pieceQty?.unsellableQty ?? 0;
    tileData.stockDetails.unsellableQty.cs += caseQty?.unsellableQty ?? 0;
    tileData.stockDetails.unsellableQty.other += otherQty?.unsellableQty ?? 0;
    tileData.stockDetails.unsellableQty.amount += calculateTotalAmount(
      catalogueItem,
      caseQty?.unsellableQty,
      pieceQty?.unsellableQty,
      otherQty?.unsellableQty,
      distributorPricing
    );

    tileData.stockDetails.inTransitQty.pc += pieceQty?.inTransitQty ?? 0;
    tileData.stockDetails.inTransitQty.cs += caseQty?.inTransitQty ?? 0;
    tileData.stockDetails.inTransitQty.other += otherQty?.inTransitQty ?? 0;
    tileData.stockDetails.inTransitQty.amount += calculateTotalAmount(
      catalogueItem,
      caseQty?.inTransitQty,
      pieceQty?.inTransitQty,
      otherQty?.inTransitQty,
      distributorPricing
    );
  });

  calculateTotalSummary(tileData);
  return tileData;
};

export const defaultStockSummaryTileData: StockSummaryData = {
  totalStockSummary: {
    totalCs: 0,
    totalPc: 0,
    totalOther: 0,
    totalAmount: 0,
  },
  stockDetails: {
    availableQty: {
      cs: 0,
      pc: 0,
      other: 0,
      amount: 0,
    },
    committedQty: {
      cs: 0,
      pc: 0,
      other: 0,
      amount: 0,
    },
    onRouteQty: {
      cs: 0,
      pc: 0,
      other: 0,
      amount: 0,
    },
    returnedQty: {
      cs: 0,
      pc: 0,
      other: 0,
      amount: 0,
    },
    unsellableQty: {
      cs: 0,
      pc: 0,
      other: 0,
      amount: 0,
    },
    inTransitQty: {
      cs: 0,
      pc: 0,
      other: 0,
      amount: 0,
    },
  },
};

interface StyleUtilsType {
  [key: string]: {
    heading: string;
    bgColor: string;
  };
}

export const styleUtils: StyleUtilsType = {
  availableQty: { heading: "Available/Floor Stock", bgColor: "#f2fff5" },
  committedQty: { heading: "Committed/Ordered Stock", bgColor: "#edf6f7" },
  onRouteQty: { heading: "On Route Stock", bgColor: "#fbfff2" },
  returnedQty: { heading: "Returned Stock", bgColor: "#FAF8FF" },
  unsellableQty: { heading: "Unsellable Stock", bgColor: "#ffeeef" },
  inTransitQty: { heading: "In-Transit Stock", bgColor: "#FFF7F0" },
};
interface StockDetail {
  cs: number;
  pc: number;
  other: number;
  amount: number;
}

export interface StockSummaryData {
  totalStockSummary: {
    totalCs: number;
    totalPc: number;
    totalOther: number;
    totalAmount: number;
  };
  stockDetails: StockDetails;
}

interface StockDetails {
  availableQty: StockDetail;
  committedQty: StockDetail;
  onRouteQty: StockDetail;
  returnedQty: StockDetail;
  unsellableQty: StockDetail;
  inTransitQty: StockDetail;
}

// Utility function to truncate text based on cell width
export const truncateText = (text: string, truncateTextAfterChar: number): string => {
  const maxChars = truncateTextAfterChar;
  if (text?.length > maxChars) {
    return text.slice(0, maxChars - 3) + "...";
  }
  return text;
};
